<template>
  <div class="item pb-8">
    <div class="content" :class="{loading}">
      <tml-item-tray flex-ratio="1:2" no-wrap spacing="1rem">
        <template #primary>
          <component
            :is="shouldLinkToProductRoute ? 'tml-anchor' : 'div'"
            :href="productRoute"
            :title="item.name"
            class="image"
          >
            <tml-image
              v-if="imageUrl"
              :src="imageUrl"
              :alt="item.name"
              :src-set="[{width: 240, height: 240}]"
            />
          </component>
        </template>
        <template #secondary>
          <div class="options">
            <div class="description">
              <component
                :is="shouldLinkToProductRoute ? 'tml-anchor' : 'div'"
                :href="productRoute"
                :title="item.name"
              >
                <h4 class="mb-2">
                  {{ item.name }}
                </h4>
              </component>

              <p class="mb-2">
                <font-awesome-icon :icon="faTags" />
                <span>&nbsp;</span>
                <tml-currency :value="price" no-rounding />
                <span>&nbsp;</span>
                <span v-for="(option, index) in item.options" :key="index">
                  {{ option.Colour }}
                  <span v-if="option.Size">(size {{ option.Size }})</span>
                  <span v-if="index !== lastOption"> | </span>
                </span>
                <br />
                <span v-if="showDiscount && discount" class="light-text-color">
                  {{ discount }}% off (RRP
                  <tml-currency :value="item.rrpPrice" />)
                </span>
              </p>
            </div>
            <single-product-options
              v-if="showProductOptionSelector"
              v-model:size="selectedSize"
              v-model:colour="selectedColour"
              class="mb-2"
              :stock="item.stock"
              @out-of-stock="outOfStock = $event"
            />
            <div v-if="!preview">
              <div
                v-if="giftWrapOptions?.length && item.giftWrapCompatible"
                class="mb-4 flex flex-row items-center justify-start"
              >
                <tml-image
                  :src="giftWrapOptions[0].image"
                  class="w-12 h-12 mr-2"
                />
                <tml-radio-button
                  :input-value="giftWrapSelected"
                  class="flex flex-row items-center justify-start"
                  field-name="gift-wrap"
                  @update="value => (giftWrapSelected = value)"
                >
                  <template #label-after>
                    Gift wrap this item (<tml-currency
                      :value="giftWrapOptions?.[0]?.price"
                      no-rounding
                    />)
                  </template>
                </tml-radio-button>
              </div>
              <div v-if="allowChangeQuantity" class="quantity mb-2">
                <div v-if="showSelect" class="select">
                  <tml-new-form :name="`select_form_${item.key}`">
                    <tml-new-select
                      :ref="`quantity-select-${item.key}`"
                      v-model="selectedQuantity"
                      :name="`quantity-select-${item.key}`"
                      label="Quantity"
                      :options="quantityOptions"
                      :disabled="loading"
                    >
                      <template v-if="allowRemove" #prepend-option-custom>
                        <div
                          class="flex justify-start items-center w-full h-full input-padding"
                          @click="removeItem"
                        >
                          <p>0</p>
                        </div>
                      </template>
                      <template #append-option-custom>
                        <div
                          class="flex justify-start items-center w-full h-full input-padding"
                          @click="toggleQuantity"
                        >
                          <p>10+</p>
                        </div>
                      </template>
                    </tml-new-select>
                  </tml-new-form>
                </div>
                <div v-else class="input">
                  <tml-grid
                    class="items-center center"
                    :breakpoints="{xs: 2, md: 3}"
                    min-item-length="95px"
                  >
                    <tml-form
                      class="form"
                      :form-name="`input_form_${item.key}`"
                    >
                      <tml-input
                        v-model="selectedQuantityValue"
                        type="number"
                        class="my-0"
                        :field-name="`quantity_input_${item.key}`"
                        placeholder="Quantity"
                        @on-submit="updateItem"
                        @blur="quantityChanged"
                      />
                    </tml-form>
                    <tml-anchor
                      v-if="!updateOnQuantityChange"
                      href="#"
                      :icon="faEdit"
                      text="Update"
                      :disabled="!showUpdate"
                      @click="updateItem"
                    />
                  </tml-grid>
                </div>
              </div>

              <div
                v-if="
                  theme().get('checkout.shipping.showEstimates') === '1' &&
                  !stockConfigLoading
                "
                class="my-2"
              >
                <div v-if="dispatchDays > 0">
                  <font-awesome-icon class="mr-2" :icon="faHourglassStart" />
                  <span>{{ dispatchDaysMessage }}</span>
                </div>
                <div v-else>
                  <font-awesome-icon class="mr-2" :icon="faCheck" />
                  <span>In stock now</span>
                </div>
              </div>
              <tml-anchor
                v-else-if="allowRemove"
                href="#"
                :icon="faTrashAlt"
                text="Remove"
                @click="removeItem"
              />
              <div class="item-button">
                <tml-button
                  v-if="showButton"
                  class="mt-4"
                  :text="buttonText"
                  primary
                  fill
                  @click="handleButtonEvent"
                />
              </div>
            </div>
            <div v-else>
              <p v-if="showQuantityCount">Quantity: {{ item.quantity }}</p>
              <p v-if="item.giftWrapOptionId" class="mt-2">
                Gift wrapped (<tml-currency
                  :value="giftWrapOptions?.[0]?.price"
                  no-rounding
                />)
              </p>
              <div
                v-if="
                  theme().get('checkout.shipping.showEstimates') === '1' &&
                  !stockConfigLoading
                "
                class="my-2"
              >
                <div v-if="dispatchDays > 0">
                  <font-awesome-icon class="mr-2" :icon="faHourglassStart" />
                  <span>{{ dispatchDaysMessage }}</span>
                </div>
                <div v-else>
                  <font-awesome-icon class="mr-2" :icon="faCheck" />
                  <span>In stock now</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </tml-item-tray>
    </div>
  </div>
</template>

<script>
import {
  faTags,
  faTrashAlt,
  faGift,
  faEdit,
  faCheck,
  faHourglassStart,
} from '@fortawesome/pro-light-svg-icons';
import {
  formatUrl,
  convertImageUrlToWebPIfSupported,
  viewportSize,
} from '@teemill/common/helpers';
import SingleProductOptions from './SingleProductOptions.vue';
import {isObject} from 'lodash';
import {formatCurrency} from '@teemill/utilities';

export default {
  name: 'CartItemForm',

  components: {SingleProductOptions},

  inject: ['app', 'theme'],

  props: {
    item: {
      type: Object,
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    allowChangeQuantity: {
      type: Boolean,
      default: true,
    },
    showButton: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Add To Cart',
    },
    allowRemove: {
      type: Boolean,
      default: true,
    },
    allowOptionsChange: {
      type: Boolean,
      default: false,
    },
    showDiscount: {
      type: Boolean,
      default: false,
    },
    includeTax: {
      type: Boolean,
      default: false,
    },
    useQuantitySelect: {
      type: Boolean,
      default: true,
    },
    updateOnQuantityChange: {
      type: Boolean,
      default: false,
    },
    dispatchDays: {
      type: Number,
      default: 0,
    },
    stockConfigLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      faTags,
      faTrashAlt,
      faGift,
      faEdit,
      faHourglassStart,
      faCheck,
      loading: false,
      showSelect: true,
      selectedQuantityValue: null,
      quantityOptions: [
        {text: '1', value: 1},
        {text: '2', value: 2},
        {text: '3', value: 3},
        {text: '4', value: 4},
        {text: '5', value: 5},
        {text: '6', value: 6},
        {text: '7', value: 7},
        {text: '8', value: 8},
        {text: '9', value: 9},
      ],

      selectedColour: undefined,
      selectedSize: undefined,
      outOfStock: false,

      giftWrapSelected: false,

      previousColour: undefined,
      previousSize: undefined,
      previousQuantity: undefined,
      previousGiftWrapSelected: undefined,
    };
  },

  computed: {
    /**
     * Ensures the value passed to the select is always an object and the quantity value is
     * always an integer.
     */
    selectedQuantity: {
      set(option) {
        if (!option) {
          this.selectedQuantityValue = parseInt(this.item.quantity, 10);
        } else if (isObject(option)) {
          this.selectedQuantityValue = parseInt(option.value, 10);
        } else {
          this.selectedQuantityValue = parseInt(option, 10);
        }
      },
      get() {
        const option = this.quantityOptions.find(
          q => q.value === this.selectedQuantityValue
        );

        return option ?? null;
      },
    },

    /**
     * When showSelect equals false then an input is shown, this property shows an update
     * button when the input value doesnt match the current item quantity.
     */
    showUpdate() {
      return this.item.quantity !== parseInt(this.selectedQuantityValue, 10);
    },

    lastOption() {
      return Object.keys(this.item.options).length - 1;
    },

    price() {
      return this.includeTax ? this.item.priceIncTax : this.item.price;
    },

    discount() {
      const defaultPrice = this.item.rrpPrice;

      return Math.round(((defaultPrice - this.price) / defaultPrice) * 100);
    },

    productRoute() {
      return `/product/${this.item.productUrl}`;
    },

    imageUrl() {
      const image = this.item.image;

      if (!image) {
        return '';
      }

      return formatUrl(image);
    },

    webPImageUrl() {
      if (!this.imageUrl) {
        return '';
      }

      return convertImageUrlToWebPIfSupported(this.imageUrl).srcSet([
        {width: 240, height: 240},
      ]);
    },

    shouldLinkToProductRoute() {
      return this.app.isSubdomain && !this.item.preview;
    },

    showProductOptionSelector() {
      return (
        this.allowOptionsChange &&
        this.selectedSize &&
        this.selectedColour &&
        this.item.stock
      );
    },

    showQuantityCount() {
      return this.preview || this.item.quantity > 1;
    },

    mobileView() {
      return viewportSize.isSmaller('md');
    },

    isRapanui: () => $store.state.subdomain?.division === 14,

    giftWrapOptions() {
      return (
        $store.state.subdomain?.giftWrapOptions?.flatMap(product => {
          return product.variants.map(variant => {
            return {
              text: `${product.name} - ${variant.name}`,
              id: variant.id,
              price: variant.price,
              image: variant.image,
            };
          });
        }) || []
      );
    },

    currency() {
      return this.$store.getters['currency/get'](null)?.symbol;
    },

    hasApplications() {
      return Boolean(this.item.hasApplications);
    },

    dispatchDaysMessage() {
      if (this.hasApplications) {
        return `Personalised items can take up to ${this.dispatchDays} days`;
      }

      return `Available in ${this.dispatchDays} days`;
    },
  },

  watch: {
    selectedSize(newSize) {
      if (newSize === this.previousSize) {
        return;
      }

      this.updateItem();
    },

    selectedColour(newColour) {
      if (newColour === this.previousColour) {
        return;
      }

      this.updateItem();
    },

    selectedQuantity(newQuantity) {
      if (!this.showSelect || newQuantity.value === this.previousQuantity) {
        return;
      }

      this.updateItem();
    },

    giftWrapSelected(newGiftWrapSelected) {
      if (newGiftWrapSelected === this.previousGiftWrapSelected) {
        return;
      }

      this.updateItem();
    },
  },
  created() {
    if (this.item.options) {
      this.previousColour = this.item.options[0]['Colour'];
      this.selectedColour = this.item.options[0]['Colour'];
      this.previousSize = this.item.options[0]['Size'];
      this.selectedSize = this.item.options[0]['Size'];
    }

    this.previousQuantity = this.item.quantity;
    this.selectedQuantityValue = this.item.quantity;
    this.previousGiftWrapSelected = !!this.item.giftWrapOptionId;
    this.giftWrapSelected = !!this.item.giftWrapOptionId;
  },

  mounted() {
    // Logic that determines whether to show the select or input
    if (this.item.quantity > 9 || !this.useQuantitySelect) {
      this.selectedQuantityValue = this.item.quantity;
      this.showSelect = false;
    }
  },
  methods: {
    removeItem() {
      // Closes the select dropdown immediately
      if (this.$refs[`quantity-select-${this.item.key}`]) {
        this.$refs[`quantity-select-${this.item.key}`].close();
      }

      if (!this.allowRemove) {
        return;
      }

      this.loading = true;

      const promise = new Promise((resolve, reject) => {
        this.$emit('remove-item', this.item.key, resolve, reject);
      });

      promise
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    updateItem() {
      const dataUnchanged =
        this.selectedSize === this.previousColour &&
        this.selectedColour === this.previousColour &&
        this.selectedQuantityValue === this.previousQuantity &&
        this.giftWrapSelected === this.previousGiftWrapSelected;

      if (dataUnchanged || this.outOfStock) {
        return;
      }

      this.loading = true;

      if (this.item.step && this.item.step > 1) {
        this.selectedQuantityValue =
          Math.ceil(this.selectedQuantityValue / this.item.step) *
          this.item.step;
      }

      const itemData = {
        giftWrapOptionId: this.giftWrapSelected
          ? this.giftWrapOptions?.[0]?.id || null
          : null,
        quantity: this.selectedQuantityValue,
        colour: this.selectedColour,
        size: this.selectedSize,
      };

      const promise = new Promise((resolve, reject) => {
        this.$emit('update-item', this.item.key, itemData, resolve, reject);
      });

      promise
        .then(item => {
          if (item) {
            this.previousColour = item.options[0]['Colour'];
            this.previousSize = item.options[0]['Size'];
            this.previousQuantity = item.quantity;
            this.previousGiftWrapSelected = !!item.giftWrapOptionId;

            this.selectedQuantityValue = item.quantity;
          }

          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    toggleQuantity() {
      this.showSelect = !this.showSelect;
    },

    quantityChanged() {
      if (this.updateOnQuantityChange) {
        this.updateItem();
      }
    },

    handleButtonEvent() {
      this.loading = true;
      const promise = new Promise((resolve, reject) => {
        this.$emit('handle-button', this.item.key, resolve, reject);
      });

      promise
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    formatUrl,
    formatCurrency,
  },
};
</script>
<style lang="scss" scoped>
.item {
  .quantity {
    .select {
      max-width: 95px;
    }
  }

  .content {
    max-width: 500px;

    &.loading {
      opacity: 0.5;
    }
  }
}
</style>
